import { BuyBoosterPopupLayout } from '../../../../Layouts/BuyBoosterPopupLayout';
import BaseMenu from '../BaseMenu';
import BaseUISystem from '../../BaseUISystem';
import { UIActions } from '../../../../State/UI/UIActions';
import { PreBoosterType } from '../../../../State/Card/BoosterType';
import SpriteComponent from '../../../../Component/Render/Sprite';
import { iconByPreBoosterType } from './LevelPopupBooster';
import TextComponent from '../../../../Component/Render/Text';
import Game from '../../../../Game';
import { GameObjectEvent } from '../../../../GameObject/GameObject';
import LevelStateHelper from '../../../../State/Level/LevelStateHelper';
import { applyDefaultButtonAnimation } from '../../UIHelper';
import { PlayerActions } from '../../../../State/Player/PlayerActions';
import { isShopSupported } from '../../../../Utility/Platform';
import { GameActions } from '../../../../State/Game/GameActions';
import { PopupID } from '../../../../State/UI/PopupID';
import { hackForceCurrencyAnimation } from '../../../../Prefabs/UI/HUD/CoinBar';
import { sendEvent, track, TrackMethod } from 'Utility/Tracking/Track';
import { BoosterTypeMap } from 'State/Card/BoosterTypeMap';
import { TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { ResourceTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { Layout } from '../../../../Utility/layout/createLayout';

const headerKeyByPreBoosterType: Record<PreBoosterType, string> = {
    [PreBoosterType.REMOVE_THREE]: 'RemoveThreePreBoosterShortName',
    [PreBoosterType.JOKER]: 'JokerPreBoosterShortName',
    [PreBoosterType.CANISTER]: 'CanisterPreBoosterShortName',
};

const titleKeyByPreBoosterType: Record<PreBoosterType, string> = {
    [PreBoosterType.REMOVE_THREE]: 'RemoveThreePreBoosterTitle',
    [PreBoosterType.JOKER]: 'JokerPreBoosterTitle',
    [PreBoosterType.CANISTER]: 'CanisterPreBoosterTitle',
};

type OnClose = (bought: boolean) => void;

type BuyBoosterPopupProps = {
    type: PreBoosterType;
    onClose: OnClose;
}

export class BuyBoosterPopup extends BaseMenu {
    private layout: any;
    private type: PreBoosterType;
    private priceA: number;
    private priceB: number;
    private onClose: OnClose;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.BUY_BOOSTER_POPUP, uiSystem, false);
    }

    public onShow(props: BuyBoosterPopupProps): void {
        this.type = props.type;
        this.onClose = props.onClose;

        this.layout = new BuyBoosterPopupLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        const icon = iconByPreBoosterType[this.type];
        this.layout.iconA.getComponent(SpriteComponent).texture = icon;
        this.layout.iconB.getComponent(SpriteComponent).texture = icon;

        this.layout.headerText.getComponent(TextComponent).text =
            Game.locale.getText(headerKeyByPreBoosterType[this.type]);

        this.layout.titleText.getComponent(TextComponent).text =
            Game.locale.getText(titleKeyByPreBoosterType[this.type]);

        const price = LevelStateHelper
            .getBoosterPrice(this.type, Game.player.getHighestLevelCompleted());

        this.priceA = price;
        this.priceB = Math.round(price * 3 * 0.9);
        this.layout.priceA.getComponent(TextComponent).text = `${this.priceA}`;
        this.layout.priceB.getComponent(TextComponent).text = `${this.priceB}`;
        this.layout.character.addComponent(RelativePositionComponent);
        this.layout.character.getComponent(RelativePositionComponent).y = 0;

        this.layout.buyButtonA.on(GameObjectEvent.SELECTED, this.handleBuyClickA);
        this.layout.buyButtonB.on(GameObjectEvent.SELECTED, this.handleBuyClickB);
        this.layout.closeButton.on(GameObjectEvent.SELECTED, this.handleCloseClick);

        [this.layout.buyButtonA, this.layout.buyButtonB, this.layout.closeButton]
            .forEach((button) => applyDefaultButtonAnimation(button));
    }

    private readonly handleBuyClickA = (): void => {
        UIActions.hidePopup(this.name);
        this.tryToBuyBooster(1, this.priceA);
    };

    private readonly handleBuyClickB = (): void => {
        UIActions.hidePopup(this.name);
        this.tryToBuyBooster(3, this.priceB);
    };

    private readonly handleCloseClick = (): void => {
        UIActions.hidePopup(this.name);
        this.onClose(false);
    };

    private async tryToBuyBooster(amount: number, price: number): Promise<void> {
        if (this.system.store.getState().playerState.inventory.coins >= price) {
            this.buyBooster(amount, price);
            this.onClose(true);
        } else if (isShopSupported()) {
            await Promise.race([
                Game.store.waitFor(GameActions.restoreContext),
                new Promise((resolve) => {
                    UIActions.showPopup(PopupID.SHOP_MENU, {
                        onClose: resolve,
                    });
                })
            ]);

            if (this.system.store.getState().playerState.inventory.coins >= price) {
                this.buyBooster(amount, price);
                this.onClose(true);
            } else {
                this.onClose(false);
            }
        } else {
            UIActions.showPopup(PopupID.NO_COINS_MENU, {
                onClose: () => this.onClose(false),
            });
        }
    }

    private buyBooster(amount: number, price: number): void {
        const state = this.system.store.getState();
        hackForceCurrencyAnimation();
        try {
            track(BoosterTypeMap[this.type].receivedEvent, amount, TrackMethod.ADD);
            track('transactionType', TransactionType.COIN_PURCHASE);
            track(BoosterTypeMap[this.type].coinsEvent, price, TrackMethod.ADD);
            track('mCoinsSpent', price, TrackMethod.ADD);
            sendEvent(ResourceTrackingEventName.resource, state);
        } catch (e) {
            console.warn(e);
        }


        PlayerActions.setPlayerState({
            ...state.playerState,
            inventory: {
                ...state.playerState.inventory,
                [this.type]: state.playerState.inventory[this.type] + amount,
                coins: state.playerState.inventory.coins - price,
            },
        });
    }
}
